import React, { createContext, useContext, useRef, useState } from "react";
import { Command, parseMessage } from "./communicationDefinitions";
import {
  DeviceProfile,
  formatCommand,
  formatProfile,
  PourOverPattern,
  Shake,
  StageData,
} from "./profile";
import { Buffer } from "buffer";

export interface MachineState {
  weight: number;
  waterDispensed: number;
  screen: number;
  grindAdjustment: number;
  profileLoaded: boolean
  brewingStarted: boolean;
}


interface XBloomContextType {
  connect: () => Promise<void>;
  isConnected: boolean;
  machineState: MachineState;
  loadProfile: () => Promise<void>;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  command: (cmd: Command) => void;
}

const XBloomContext = createContext<XBloomContextType | undefined>(undefined);


export const useXBloom = () => {
  const context = useContext(XBloomContext);
  if (context === undefined) {
    throw new Error('useXBloom must be used within an XBloomProvider');
  }
  return context;
};

export const XBloomProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const server = useRef<BluetoothRemoteGATTServer | null>(null);
  const rxCharacteristic = useRef<BluetoothRemoteGATTCharacteristic | null>(null);
  const txCharacteristic = useRef<BluetoothRemoteGATTCharacteristic | null>(null);
  const rwxCharacteristic = useRef<BluetoothRemoteGATTCharacteristic | null>(null);
  
  const [machineState, setMachineState] = useState<MachineState>({
    weight: 0,
    waterDispensed: 0,
    screen: 0,
    grindAdjustment: 0,
    profileLoaded: false,
    brewingStarted: false,
  });

  const connect = async () => {
    let device = null;
    try {
      device = await navigator.bluetooth.requestDevice({
        filters: [
          {
            namePrefix: "XBLOOM",
          },
        ],
        optionalServices: ["0000e0ff-3c17-d293-8e48-14fe2e4da212"],
      });
    } catch (error) {
      return;
    }
    
    if (!device || !device.gatt) {
      return;
    }
    
    server.current = await device.gatt?.connect();

    const service = await server.current.getPrimaryService(
      "0000e0ff-3c17-d293-8e48-14fe2e4da212"
    );

    const TXChar = await service.getCharacteristic(
      "0000ffe1-0000-1000-8000-00805f9b34fb"
    );

    const RXChar = await service.getCharacteristic(
      "0000ffe2-0000-1000-8000-00805f9b34fb"
    );
    
    const RWXChar = await service.getCharacteristic(
      "0000ffe3-0000-1000-8000-00805f9b34fb"
    );
    
    rxCharacteristic.current = RXChar;
    txCharacteristic.current = TXChar;
    rwxCharacteristic.current = RWXChar;

    RWXChar.addEventListener("characteristicvaluechanged", (event: Event) => {
      let characteristic = event.target as BluetoothRemoteGATTCharacteristic;
      const msg = new Uint8Array(characteristic.value!.buffer);
      console.log("RWX char:", Buffer.from(msg).toString("hex"));
    });
    RWXChar.startNotifications();

    RXChar.addEventListener("characteristicvaluechanged", (event: Event) => {
      let characteristic = event.target as BluetoothRemoteGATTCharacteristic;
      const msg = parseMessage(characteristic.value!);
      if (!msg) return;

      setMachineState((prev) => {
        const newState = { ...prev };
        
        if (msg.info_weight !== undefined) {
          newState.weight = msg.info_weight;
        }
        if (msg.info_machine !== undefined) {
          newState.screen = msg.info_machine;
        }
        if (msg.info_grind_adjust !== undefined) {
          newState.grindAdjustment = msg.info_grind_adjust;
        }
        if (msg.info_brewing !== undefined) {
          newState.waterDispensed = msg.info_brewing;
        }
        if (msg.info_ack === "recipe") {
          newState.profileLoaded = true;
        }
        if (msg.info_ack === "start") {
          newState.brewingStarted = true;
        }
        
        return newState;
      });
    });
    RXChar.startNotifications();

    setTimeout(async () => {
      const pair = new Uint8Array([
        0x58, 0x01, 0x01, 0xa4, 0x1f, 0x10, 0x00, 0x00, 0x00, 0x01, 0x01, 0x00,
        0x00, 0x00, 0x02, 0x43,
      ]);
      if (!txCharacteristic.current) {
        console.error("txCharacteristic is null, not sending pair");
        return;
      }
      await txCharacteristic.current.writeValueWithoutResponse(pair);
    }, 300);
    
    setConnected(true);
  };

  const loadProfile = async () => {
    setMachineState((prev) => ({...prev, profileLoaded: false}));
    const stages: StageData[] = [
      {
        weight: 50,
        temperature: 95,
        pattern: PourOverPattern.CIRCLE,
        shake: Shake.AFTER,
        time: 30,
        grindRPM: 60,
        flowRate: 3,
      },
      {
        weight: 100,
        temperature: 95,
        pattern: PourOverPattern.CIRCLE,
        shake: Shake.NONE,
        time: 5,
        grindRPM: 0,
        flowRate: 3.5,
      },
      {
        weight: 100,
        temperature: 93,
        pattern: PourOverPattern.CIRCLE,
        shake: Shake.NONE,
        time: 0,
        grindRPM: 0,
        flowRate: 3.5,
      },
    ];
    
    const profile: DeviceProfile = {
      stages,
      grindSize: 45,
      brewRatio: 13.9,
    };

    if (!txCharacteristic.current) {
      console.error("txCharacteristic is null");
      setConnected(false);
      return;
    }

    // Send initialization commands
    setTimeout(async () => {
      const unknown1 = new Uint8Array([
        0x58, 0x01, 0x01, 0xa6, 0x1f, 0x18, 0x00, 0x00, 0x00, 0x01,
        0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x12, 0x00, 0x00, 0x00,
        0x7e, 0xb5,
      ]);
      await txCharacteristic.current?.writeValueWithoutResponse(unknown1);
    }, 500);

    setTimeout(async () => {
      const unknown2 = new Uint8Array([
        0x58, 0x01, 0x01, 0xa8, 0x1f, 0x14, 0x00, 0x00, 0x00, 0x01,
        0x00, 0x00, 0xdc, 0x42,
        0x00, 0x00, 0xb4, 0x42,
        0x21, 0xa1,
      ]);
      await txCharacteristic.current?.writeValueWithoutResponse(unknown2);
    }, 1000);

    setTimeout(async () => {
      const profileBytes = formatProfile(profile);
      await txCharacteristic.current?.writeValueWithoutResponse(profileBytes);
    }, 1500);
  };

  const start = async () => {
    if (!txCharacteristic.current) {
      console.error("txCharacteristic is null");
      setConnected(false);
      return;
    }
    const startCMD = formatCommand(Command.CMD_START_RECIPE);
    await txCharacteristic.current.writeValueWithoutResponse(startCMD);
  };

  const stop = async () => {
    if (!txCharacteristic.current) {
      console.error("txCharacteristic is null");
      setConnected(false);
      return;
    }
    const stopCMD = formatCommand(Command.CMD_PAUSE);
    await txCharacteristic.current.writeValueWithoutResponse(stopCMD);
  };

  const command = async (cmd: Command) => {
    if (!txCharacteristic.current) {
      console.error("txCharacteristic is null");
      setConnected(false);
      return;
    }
    const cmdBytes = formatCommand(cmd);
    await txCharacteristic.current.writeValueWithoutResponse(cmdBytes);
  }

  const value = {
    connect,
    isConnected: connected,
    machineState,
    loadProfile,
    start,
    stop,
    command
  };

  return (
    <XBloomContext.Provider value={value}>
      {children}
    </XBloomContext.Provider>
  );
};